import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecoveryConfig, saveRecoveryConfig, getSchoolYear } from '../../Api';
import LoadingSpinner from '../../components/Loading';

const REGRAS = [
    { id: 'maior', titulo: 'Fica a maior nota', exemplo: 'Período 10, recuperação 18 → fica 18' },
    { id: 'media', titulo: 'Média entre as duas notas', exemplo: 'Período 10, recuperação 18 → fica 14 (nunca abaixo da nota do período)' },
    { id: 'ate_media', titulo: 'Recuperação vale até a média', exemplo: 'Período 10, recuperação 18, média 15 → fica 15' },
];

const EXEMPLO_FINAL = {
    maior: 'Total do ano 50, recuperação final 80 → fica 80',
    media: 'Total do ano 50, recuperação final 80 → fica 65 (nunca abaixo do total do ano)',
    ate_media: 'Total do ano 50, recuperação final 80, média anual 60 → fica 60',
};

const card = {
    width: '100%', maxWidth: 720, boxSizing: 'border-box', margin: '0 auto',
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
    border: '1px solid var(--line, #e3e8eb)', borderRadius: 14, padding: '22px 22px 18px',
};

const RecoveryConfig = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [idSchool, setIdSchool] = useState(null);
    const [ano, setAno] = useState('');
    const [ativa, setAtiva] = useState(true);
    const [regra, setRegra] = useState('maior');
    const [finalAtiva, setFinalAtiva] = useState(true);
    const [regraFinal, setRegraFinal] = useState('ate_media');
    const [jaConfigurada, setJaConfigurada] = useState(false);
    const [msg, setMsg] = useState(null);

    const podeEditar = ['DIRETOR/SUPERVISOR', 'SECRETARIO'].includes(localStorage.getItem('position_at_school'));

    useEffect(() => {
        (async () => {
            try {
                const id = JSON.parse(sessionStorage.getItem('id-school') || 'null');
                setIdSchool(id);
                const sy = await getSchoolYear(id);
                const year = String(sy?.data?.data || '');
                setAno(year);
                const r = await getRecoveryConfig(id, year);
                const cfg = r?.data?.config;
                if (cfg) {
                    setJaConfigurada(true);
                    setAtiva(cfg.paralela?.ativa !== false);
                    setRegra(cfg.paralela?.regra || 'maior');
                    setFinalAtiva(cfg.final ? cfg.final.ativa !== false : true);
                    setRegraFinal(cfg.final?.regra || 'ate_media');
                }
            } catch (e) {
                setMsg({ tipo: 'erro', texto: e?.response?.data?.msg || 'Não foi possível carregar a configuração.' });
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const salvar = async () => {
        setSalvando(true);
        setMsg(null);
        try {
            await saveRecoveryConfig(idSchool, ano, { ativa, regra }, { ativa: finalAtiva, regra: regraFinal });
            setJaConfigurada(true);
            setMsg({ tipo: 'ok', texto: 'Recuperação configurada com sucesso.' });
        } catch (e) {
            setMsg({ tipo: 'erro', texto: e?.response?.data?.msg || 'Erro ao salvar a configuração.' });
        } finally {
            setSalvando(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div style={{ width: '100%', padding: '16px', boxSizing: 'border-box' }}>
            <div style={card}>
                <h2 style={{ margin: '0 0 4px' }}>🔁 Recuperação paralela — {ano}</h2>
                <p style={{ margin: '0 0 16px', color: 'var(--text-muted, #5b6b72)', fontSize: 14 }}>
                    Depois que o diário do período é fechado, o professor lança a recuperação dos alunos que
                    ficaram abaixo da média. A recuperação <strong>não conta nos pontos do período</strong>:
                    a nota final é calculada pela regra abaixo e a nota original fica guardada.
                </p>

                {!jaConfigurada && (
                    <div style={{ background: 'var(--orange-soft, #fff1e0)', border: '1px solid var(--orange, #e67e22)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
                        ⚠️ A recuperação deste ano ainda não foi configurada. Revise as opções e clique em <strong>Salvar</strong>.
                    </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, margin: '4px 0 16px', cursor: podeEditar ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={ativa} disabled={!podeEditar} onChange={(e) => setAtiva(e.target.checked)} style={{ width: 18, height: 18 }} />
                    Usar recuperação paralela (por período)
                </label>

                <div style={{ opacity: ativa ? 1 : 0.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Como fica a nota final do período</div>
                    {REGRAS.map((r) => (
                        <label key={r.id} style={{
                            display: 'block', padding: '12px 14px', marginBottom: 8, borderRadius: 10, cursor: podeEditar && ativa ? 'pointer' : 'default',
                            border: `2px solid ${regra === r.id ? 'var(--teal, #003e4f)' : 'var(--line, #e3e8eb)'}`,
                        }}>
                            <input type="radio" name="regra" value={r.id} checked={regra === r.id} disabled={!podeEditar || !ativa}
                                onChange={() => setRegra(r.id)} style={{ marginRight: 8 }} />
                            <strong>{r.titulo}</strong>
                            <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)', marginLeft: 24 }}>{r.exemplo}</div>
                        </label>
                    ))}
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--line, #e3e8eb)', margin: '18px 0 14px' }} />

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, margin: '4px 0 6px', cursor: podeEditar ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={finalAtiva} disabled={!podeEditar} onChange={(e) => setFinalAtiva(e.target.checked)} style={{ width: 18, height: 18 }} />
                    Usar recuperação final (anual)
                </label>
                <p style={{ margin: '0 0 12px', color: 'var(--text-muted, #5b6b72)', fontSize: 13 }}>
                    Vai para a recuperação final quem fechar o ano abaixo da <strong>média anual</strong> (soma das médias dos períodos).
                    O professor lança a nota na aba Recuperação <strong>antes de fechar o diário do último período</strong>.
                    A nota que fica entra na ficha anual, no histórico, no diário final e nos boletins finais.
                </p>
                <div style={{ opacity: finalAtiva ? 1 : 0.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Como fica a nota do ano após a recuperação final</div>
                    {REGRAS.map((r) => (
                        <label key={r.id} style={{
                            display: 'block', padding: '12px 14px', marginBottom: 8, borderRadius: 10, cursor: podeEditar && finalAtiva ? 'pointer' : 'default',
                            border: `2px solid ${regraFinal === r.id ? 'var(--teal, #003e4f)' : 'var(--line, #e3e8eb)'}`,
                        }}>
                            <input type="radio" name="regraFinal" value={r.id} checked={regraFinal === r.id} disabled={!podeEditar || !finalAtiva}
                                onChange={() => setRegraFinal(r.id)} style={{ marginRight: 8 }} />
                            <strong>{r.titulo}</strong>
                            <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)', marginLeft: 24 }}>{EXEMPLO_FINAL[r.id]}</div>
                        </label>
                    ))}
                </div>

                {msg && (
                    <div style={{
                        margin: '10px 0', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                        background: msg.tipo === 'ok' ? 'var(--green-soft, #e3f4ec)' : 'var(--red-soft, #fbe9e9)',
                        color: msg.tipo === 'ok' ? 'var(--green, #1d8b5a)' : 'var(--red, #cf3a3a)',
                    }}>{msg.texto}</div>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
                    <button type="button" onClick={() => navigate(-1)} style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid var(--line, #d0d7db)', background: 'transparent', color: 'var(--text, #16323b)', fontWeight: 700, cursor: 'pointer' }}>
                        Voltar
                    </button>
                    {podeEditar && (
                        <button type="button" onClick={salvar} disabled={salvando} style={{ padding: '11px 24px', borderRadius: 10, border: 'none', background: 'var(--teal, #003e4f)', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecoveryConfig;
